import { useRef, useState, useEffect } from "react";
import { CircularProgress, Chip, IconButton } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useConteosEscaneo } from "../../context/ConteosEscaneo/ConteosEscaneoContext";

const normalizarCodigo = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .toUpperCase();

const DEBOUNCE_MS = 800;

export default function SizeSoftXCodigoBarrasCamaraConteos() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const scanAnimationRef = useRef(null);
  const scanBusyRef = useRef(false);
  const ultimoCodigoRef = useRef("");
  const ultimoTimestampRef = useRef(0);

  const [camaraActiva, setCamaraActiva] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [ultimoCodigo, setUltimoCodigo] = useState("");
  const [capturando, setCapturando] = useState(false);

  const {
    handleBuscarArticuloEnInventarioActivo,
    handleCrearScanDesdeArticulo,
  } = useConteosEscaneo();

  const getErrorMessage = (err) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return "Este navegador no permite abrir la cámara desde la web.";
    }
    if (!window.isSecureContext) {
      return "La cámara requiere HTTPS o localhost.";
    }
    if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
      return "Permiso de cámara denegado.";
    }
    if (err?.name === "NotFoundError" || err?.name === "DevicesNotFoundError") {
      return "No se encontró una cámara disponible.";
    }
    if (err?.name === "NotReadableError" || err?.name === "TrackStartError") {
      return "La cámara está ocupada por otra aplicación.";
    }
    return "No se pudo iniciar la cámara.";
  };

  const detenerCamara = () => {
    if (scanAnimationRef.current) {
      cancelAnimationFrame(scanAnimationRef.current);
      scanAnimationRef.current = null;
    }
    const activeStream = streamRef.current || videoRef.current?.srcObject;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => track.stop());
    }
    streamRef.current = null;
    detectorRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    ultimoCodigoRef.current = "";
    setCameraStatus("idle");
  };

  const iniciarCamara = async () => {
    try {
      setError(null);
      setCameraStatus("requesting");

      if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
        throw new Error("CAMERA_UNAVAILABLE");
      }

      detenerCamara();

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play().catch(() => {});
      }

      setCameraStatus("ready");
      iniciarEscaneo();
    } catch (err) {
      detenerCamara();
      setCameraStatus("error");
      setError(getErrorMessage(err));
    }
  };

  const procesarCodigo = async (codigoCrudo) => {
    const codigoBarras = normalizarCodigo(codigoCrudo);
    if (!codigoBarras) return;

    const articulo = await handleBuscarArticuloEnInventarioActivo(codigoBarras);
    if (articulo) {
      await handleCrearScanDesdeArticulo(articulo);
    }
  };

  const formatosPreferidos = ["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "itf"];

  const prepararDetector = async () => {
    if (!("BarcodeDetector" in window)) return null;
    if (!detectorRef.current) {
      try {
        const formatos = await window.BarcodeDetector.getSupportedFormats?.();
        const soportados = formatos?.filter((f) => formatosPreferidos.includes(f)) || formatosPreferidos;
        detectorRef.current = new window.BarcodeDetector({ formats: soportados });
      } catch {
        detectorRef.current = new window.BarcodeDetector();
      }
    }
    return detectorRef.current;
  };

  const handleCapturarManual = async () => {
    if (capturando || !videoRef.current) return;
    setCapturando(true);
    try {
      let codigoCrudo = null;
      const detector = await prepararDetector();
      if (detector) {
        const resultados = await detector.detect(videoRef.current);
        codigoCrudo = resultados?.[0]?.rawValue?.trim();
      }
      if (!codigoCrudo) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (canvas && detector) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0);
          const resultados = await detector.detect(canvas);
          codigoCrudo = resultados?.[0]?.rawValue?.trim();
        }
      }
      if (codigoCrudo) {
        setUltimoCodigo(codigoCrudo);
        await procesarCodigo(codigoCrudo);
      }
    } finally {
      setCapturando(false);
    }
  };

  const iniciarEscaneo = () => {
    let cancelado = false;

    const leerFrame = async () => {
      if (cancelado || scanBusyRef.current) {
        if (!cancelado) {
          scanAnimationRef.current = requestAnimationFrame(leerFrame);
        }
        return;
      }

      const detector = await prepararDetector();
      if (!detector || !videoRef.current || cancelado) return;

      try {
        scanBusyRef.current = true;
        const resultados = await detector.detect(videoRef.current);
        const codigoCrudo = resultados?.[0]?.rawValue?.trim();
        const ahora = Date.now();

        if (codigoCrudo && (codigoCrudo !== ultimoCodigoRef.current || ahora - ultimoTimestampRef.current > DEBOUNCE_MS)) {
          ultimoCodigoRef.current = codigoCrudo;
          ultimoTimestampRef.current = ahora;
          setUltimoCodigo(codigoCrudo);
          await procesarCodigo(codigoCrudo);
        }
      } catch {
        // ignore frame errors
      } finally {
        scanBusyRef.current = false;
      }

      if (!cancelado) {
        scanAnimationRef.current = requestAnimationFrame(leerFrame);
      }
    };

    scanAnimationRef.current = requestAnimationFrame(leerFrame);
  };

  useEffect(() => {
    if (!camaraActiva) {
      detenerCamara();
    }
  }, [camaraActiva]);

  useEffect(() => {
    return () => {
      detenerCamara();
    };
  }, []);

  const toggleCamara = () => {
    if (camaraActiva) {
      detenerCamara();
      setCamaraActiva(false);
    } else {
      setCamaraActiva(true);
      iniciarCamara();
    }
  };

  return (
    <div className="camara-codigo-barras-portatil">
      <button
        className="camara-codigo-barras-portatil__toggle"
        onClick={toggleCamara}
        disabled={cameraStatus === "requesting"}
      >
        {camaraActiva ? <VideocamOffIcon /> : <VideocamIcon />}
        <span>{camaraActiva ? "Cerrar cámara" : "Escanear con cámara"}</span>
      </button>

      {camaraActiva && (
        <div className="camara-codigo-barras-portatil__visor">
          {cameraStatus === "requesting" && (
            <div className="camara-codigo-barras-portatil__overlay">
              <CircularProgress size={24} sx={{ color: "white" }} />
              <span>Iniciando cámara...</span>
            </div>
          )}

          {cameraStatus === "error" && (
            <div className="camara-codigo-barras-portatil__overlay">
              <span className="camara-codigo-barras-portatil__error">{error}</span>
              <button className="camara-codigo-barras-portatil__retry" onClick={iniciarCamara}>
                Reintentar
              </button>
            </div>
          )}

          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="camara-codigo-barras-portatil__video"
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {cameraStatus === "ready" && (
            <>
              <Chip
                size="small"
                color="success"
                label="Escaneo continuo..."
                className="camara-codigo-barras-portatil__status"
              />
              {ultimoCodigo && (
                <Chip
                  size="small"
                  color="info"
                  label={ultimoCodigo}
                  className="camara-codigo-barras-portatil__ultimo"
                />
              )}
              <IconButton
                className="camara-codigo-barras-portatil__capturar"
                size="small"
                onClick={handleCapturarManual}
                disabled={capturando}
              >
                <CameraAltIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </div>
      )}
    </div>
  );
}
